import { Input as UIKitInput } from "@credit-acceptance/uikit";
import { type ChangeEvent, type ComponentProps, useRef } from "react";

import { expandTwoDigitYear } from "../../../lib/twoDigitYear";
import { cn } from "../../../lib/utils";

/** `YYYY-MM-DD`, the only shape the Signers schema, mapping, and validation accept. */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Parses an ISO date into a local-midnight `Date`.
 *
 * Deliberately not `new Date(iso)`, which reads a bare `YYYY-MM-DD` as UTC and
 * lands on the previous day for anyone west of Greenwich.
 * @param iso The ISO date string.
 * @returns {Date | undefined} The date, or undefined when the string is not a full ISO date.
 */
function isoToDate(iso: string): Date | undefined {
  if (!ISO_DATE.test(iso)) {
    return undefined;
  }
  const [year, month, day] = iso.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

/**
 * Formats a `Date` back to ISO using its local parts, mirroring {@link isoToDate}.
 * @param date The date to format.
 * @returns {string} The `YYYY-MM-DD` string.
 */
function dateToIso(date: Date): string {
  const year = String(date.getFullYear()).padStart(4, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** `M/D/YY` or `M/D/YYYY` — shorthand accepted on blur and expanded to the full date. */
const SHORT_DATE = /^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/;

/**
 * Parses shorthand like `8/21/26` into a real date, so the field can expand it
 * to the full `MM/DD/YYYY` on blur instead of clearing it. uikit's own
 * autoformat only pads a typed year when it already has as many digits as the
 * configured format's `yyyy` token, so a 2-digit year falls through to here.
 * @param text The raw typed text.
 * @returns {Date | undefined} The parsed date, or undefined when `text` isn't `M/D/Y(Y)`-shaped or isn't a real calendar date.
 */
function parseShortDate(text: string): Date | undefined {
  const match = SHORT_DATE.exec(text.trim());
  if (!match) {
    return undefined;
  }
  const month = Number(match[1]);
  const day = Number(match[2]);
  const year = match[3].length === 2 ? expandTwoDigitYear(Number(match[3])) : Number(match[3]);
  const date = new Date(year, month - 1, day);
  // `new Date` rolls invalid combinations like month 21 or Feb 30 forward into
  // a different date; round-tripping the parts catches that instead of
  // silently accepting a misread date.
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day ? date : undefined;
}

/**
 * Builds the empty-state hint for a date field (`MM/DD/YYYY` in en-US). uikit
 * renders nothing by default, which loses the only cue for the order of the
 * parts now that the browser's own control is gone. Uppercase to match how the
 * app writes date formats elsewhere, rather than the lowercase form browsers
 * use natively.
 * @returns {string} The placeholder in the current locale's part order.
 */
function localeDatePlaceholder(): string {
  const parts = new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(2000, 0, 2));
  return parts
    .map((part) => {
      switch (part.type) {
        case "year": {
          return "YYYY";
        }
        case "month": {
          return "MM";
        }
        case "day": {
          return "DD";
        }
        default: {
          return part.value;
        }
      }
    })
    .join("");
}

type DateInputProperties = {
  /** ISO `YYYY-MM-DD`, or `""` when empty. */
  value: string;
  /** Receives ISO `YYYY-MM-DD`, or `""` once the text stops being a valid date. */
  onChange: (value: string) => void;
  onBlur?: () => void;
  id?: string;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  /**
   * Show the "×" that empties the field. Off by default: no date field in the
   * workbench offers one, and the calendar button already occupies that corner.
   * uikit hardcodes it on, so turning it off relies on the override landing
   * after it in the spread — see the note on `allowClear` below.
   */
  allowClear?: boolean;
  /** Keep invalid typed text in form state so optional dates can report errors instead of becoming blank. */
  preserveInvalidText?: boolean;
  "aria-label"?: string;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
  "aria-required"?: boolean;
};

/**
 * uikit's DatePicker, speaking ISO at the edges.
 *
 * Replaces `<Input type="date">`, which only ever rendered the browser's native
 * date control inside uikit chrome — uikit's own `kind="date"` was never used.
 *
 * The control displays and accepts the user's locale format (`MM/dd/yyyy` in
 * en-US) but keeps the form value in ISO. That boundary is load-bearing: the
 * calculator compares dates lexicographically (`payDate < hireDate`) and slices
 * them by index (`payDate.split("-")[2] === "15"`), so anything other than
 * `YYYY-MM-DD` in the form state silently breaks validation.
 *
 * uikit reports `undefined` while the typed text is not yet a valid date; that
 * surfaces as `""`, matching what a native date input reports mid-entry.
 */
function DateInput({
  value,
  onChange,
  onBlur,
  className,
  placeholder,
  allowClear = false,
  preserveInvalidText = false,
  ...properties
}: DateInputProperties) {
  // uikit owns the display text and only replaces it when `value` changes, with
  // `""` as its documented signal to wipe it. Mid-edit that signal is
  // indistinguishable from a real clear: backspacing one character out of a
  // valid date makes uikit report `undefined`, which surfaces as `""` and comes
  // straight back as a prop, erasing the rest of what was typed. So while an
  // edit is in flight we hand uikit `undefined` — leave the text alone — and
  // reserve `""` for a clear the field did not originate.
  const editing = useRef(false);
  // Last raw text uikit reported via a real change event, kept for onBlur to
  // retry as shorthand once uikit itself gives up on the typed text.
  const rawText = useRef("");
  const date = isoToDate(value);
  if (date) {
    editing.current = false;
  }

  const uikitProperties = {
    kind: "date",
    size: "small",
    placeholder: placeholder ?? localeDatePlaceholder(),
    // uikit hardcodes `allowClear` on the DatePicker's inner InputText, but
    // spreads the caller's props after it, so passing it through wins. Kept
    // next to the other forwarded props rather than in the `...properties`
    // spread so the dependency on that ordering is visible.
    allowClear,
    // Hand uikit a Date so it skips format-matching entirely.
    value: date ?? (editing.current ? undefined : ""),
    returnType: "Date",
    className: cn("min-w-0", className),
    // uikit reports (newValue, oldValue, event); the event carries the raw
    // typed text, which onBlur below retries as `M/D/Y(Y)` shorthand once
    // uikit itself has given up on it.
    onChange: (next: Date | string | undefined, _previous?: unknown, event?: ChangeEvent<HTMLInputElement>) => {
      editing.current = !(next instanceof Date);
      if (event) {
        rawText.current = event.target.value;
      }
      onChange(next instanceof Date ? dateToIso(next) : preserveInvalidText ? rawText.current : "");
    },
    // Leaving the field ends any in-flight edit. Try the typed text as
    // shorthand (`8/21/26`) first; anything else half-typed is wiped to match
    // the `""` the form is holding.
    onBlur: () => {
      const shorthand = editing.current ? parseShortDate(rawText.current) : undefined;
      editing.current = preserveInvalidText && Boolean(rawText.current) && !shorthand;
      if (shorthand) {
        onChange(dateToIso(shorthand));
      }
      onBlur?.();
    },
    ...properties,
    // See the note in ./input.tsx — uikit's props come from an older
    // @types/react, so the DOM attribute types need reconciling.
  } as ComponentProps<typeof UIKitInput>;

  return <UIKitInput {...uikitProperties} />;
}

export { DateInput };
